// --------------------------------------------------------
// Scheduling - ScreenName
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import { Calendar, Day } from "@hassanmojab/react-modern-calendar-datepicker";
import SearchIcon from "@mui/icons-material/Search";
import { Button, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { DayValue } from "react-modern-calendar-datepicker";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../..";
import CareLineDropdown from "../../components/dropdown/CareLineDropdown";
import ErrorArea from "../../components/error/ErrorArea";
import LabelInput from "../../components/labelInput/LabelInput";
import SchedulingCard from "../../components/SchedulingCard/SechedulingCard";
import ContainerWithSidebar from "../../components/UI/containerWithSidebar/ContainerWithSidebar";
import {
  GeneralContainer,
  H1,
  H2,
  H3,
  Span,
  SubTitle,
  Text,
} from "../../components/UI/generalLayout/Layout.styled";
import { Spacer } from "../../components/UI/spacer/spacer";
import Colors from "../../constants/Colors";
import { ptBrCustomLocale } from "../../constants/Objects";
import {
  CompanyOccupationalHealth,
  CompanyService,
} from "../../core/http/service/CompanyService";
import { PersonService } from "../../core/http/service/PersonService";
import {
  ScheduleTimeAvailableData,
  SchedulingService,
} from "../../core/http/service/SchedulingService";
import {
  UserRoleService,
  UserRoleType,
} from "../../core/http/service/UserRoleService";
import { getDescricaoByCodigoAppointmentType } from "../../enums/AppointmentTypes";
import { Paths } from "../../enums/Paths";
import { setScheduleConsultType } from "../../store/actions/scheduling";
import { ILogin } from "../../store/reducers/login";
import {
  SchedulingContainer,
  SchedulingFilterContainer,
  SchedulingMedicsContainer,
} from "./Scheduling.styled";

const date = new Date();
const day: Day = {
  day: date.getDate(),
  month: date.getUTCMonth() + 1,
  year: date.getFullYear(),
};

const validPerfil: { [value: string]: true } = {
  ENF: true,
  MED: true,
  PSI: true,
};

type CompanyOccupationalHealthSelect = CompanyOccupationalHealth & {
  nome: string;
};

// --------------------------------------------------------
// Scheduling INTERFACE
// --------------------------------------------------------
/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */
function Scheduling(): JSX.Element {
  // --------------------------------------------------------
  // Scheduling PRIVATE DECLARATIONS
  // --------------------------------------------------------
  const [search, setSearch] = useState("");
  const [searchValidated, setSearchValidated] = useState<string>();
  const [selectedOccupationalHealth, setSelectedOccupationalHealth] =
    useState<CompanyOccupationalHealthSelect>();
  const [occupationalHealths, setOccupationalHealths] = useState<
    CompanyOccupationalHealthSelect[]
  >([]);
  const [roles, setRoles] = useState<UserRoleType[]>([]);
  const [selectedRole, setSelectedRole] = useState<UserRoleType>();
  const [selectedDay, setSelectedDay] = useState<DayValue>();
  const [error, setError] = useState<any[]>([]);
  const [medicoFamiliaId, setMedicoFamiliaId] = useState<string | null>(null);
  const [medicoFamiliaNome, setMedicoFamiliaNome] = useState<string | null>(null);
  const [selectedMedicoFamilia, setSelectedMedicoFamilia] =
    useState<ScheduleTimeAvailableData | undefined>();

  const [loading, setLoading] = useState(false);
  const [doctorTimeAvailable, setDoctorTimeAvailable] = useState<
    ScheduleTimeAvailableData[]
  >([]);

  const navigate = useNavigate();

  const { state } = useLocation();

  const { consultType } = (state as any) ?? {};

  const { userInfo, currentCompany } = useSelector(
    (state: { login: ILogin }) => state.login
  );

  const [allMedicosFamilia, setAllMedicosFamilia] = useState<any[]>([]);

  const dispatch = useAppDispatch();

  const onClickSearch = () => {
    if (!search || search.length > 2) {
      setSearchValidated(search || undefined);
    } else {
      setError([{ message: "O nome tem que conter mais de 3 caracteres." }]);
    }
  };

  useEffect(() => {
    const personService = new PersonService();
    personService.getMedicosFamiliaCombo().then((response: any) => {
      if (response.data.success) {
        setAllMedicosFamilia(response.data.data);
      }
    });
  }, []);

  useEffect(() => {
    if (consultType) {
      dispatch(setScheduleConsultType(consultType));
    } else {
      navigate(Paths.CollaboratorInitial);
    }
  }, [consultType, dispatch, navigate]);

  useEffect(() => {
    if (currentCompany?.id) {
      setLoading(true);
      const service = new UserRoleService();
      service
        .getAllRoles()
        .then(({ data: { data } }) => {
          const roles = data.filter(d=> { return d.profissionalSaude === true });
          roles.push({
            nome: "Todos",
            id: "",
            codigo: "",
            descricao: "",
            ordem: 0,
            atribuivel: false,
            inativo: false,
            profissionalSaude: true
          })
          setRoles(roles);
        })
        .catch((error) => setError([error]))
        .finally(() => setLoading(false));
    }
  }, [currentCompany?.id]);

  useEffect(() => {
    if (currentCompany?.id) {
      setLoading(true);
      const service = new CompanyService();
      service
        .getUnidadeAtendimento(currentCompany.id)
        .then(({ data: { data } }) => {
          const  units = data.filter(d=> {return !d.inativo})
          .map((value) => ({ ...value, nome: value.nomeFantasia }));
          units.push({ nome: "Todos", id: "", nomeFantasia: "Todos", inativo: false })
          setOccupationalHealths(units);
        })
        .catch((error) => setError([error]))
        .catch((error) => setError([error]))
        .finally(() => setLoading(false));
    }
  }, [currentCompany?.id]);

  useEffect(() => {
    const schedulingService = new SchedulingService();
    if (currentCompany?.id && userInfo?.id) {
      setLoading(true);
      setDoctorTimeAvailable([]);
      schedulingService
        .postScheduleTimeAvailable({
          empresaId: currentCompany?.id ?? "",
          pacienteId: userInfo?.id ?? "",
          profissionalNome: searchValidated,
          perfilSaude: validPerfil[selectedRole?.codigo ?? ""]
            ? selectedRole?.codigo
            : undefined,
          unidadeAtendimentoId: selectedOccupationalHealth?.id === ''? undefined : selectedOccupationalHealth?.id,
          data: selectedDay
            ? `${selectedDay.year}-${selectedDay.month}-${selectedDay.day}`
            : undefined,
          tipoConsulta: consultType,
        })
        .then((value) => {
          const results: ScheduleTimeAvailableData[] = value?.data?.data ?? [];
          if (medicoFamiliaId) {
            const familyDoctorIndex = results.findIndex(
              (d) => d.id === medicoFamiliaId
            );
            if (familyDoctorIndex > 0) {
              const [familyDoctor] = results.splice(familyDoctorIndex, 1);
              results.unshift(familyDoctor);
            }
          }
          setDoctorTimeAvailable(results);
        })
        .catch((error) => setError([error]))
        .catch((error) => setError([error]))
        .finally(() => setLoading(false));
    }
  }, [
    consultType,
    currentCompany?.id,
    medicoFamiliaId,
    searchValidated,
    selectedDay,
    selectedOccupationalHealth?.id,
    selectedRole?.codigo,
    selectedRole?.profissionalSaude,
    userInfo?.id,
  ]);

  useEffect(() => {
    if (userInfo?.id) {
      const personService = new PersonService();
      personService
        .getMedicoFamilia(userInfo.id)
        .then(({ data: { data } }: any) => {
          setMedicoFamiliaId(data?.id ?? null);
          setMedicoFamiliaNome(data?.nome ?? null);
          if (data?.id) {
            setSelectedMedicoFamilia(data);
          } else {
            setSelectedMedicoFamilia(undefined);
          }
        })
        .catch(() => {
          setMedicoFamiliaId(null);
          setMedicoFamiliaNome(null);
          setSelectedMedicoFamilia(undefined);
        });
    }
  }, [userInfo?.id]);

  const handleMedicoFamiliaChange = (doctor: ScheduleTimeAvailableData | undefined) => {
    setSelectedMedicoFamilia(doctor);
    if (userInfo?.id) {
      const personService = new PersonService();
      personService
        .updateMedicoFamilia(userInfo.id, doctor?.id ?? null)
        .then(() => {
          setMedicoFamiliaId(doctor?.id ?? null);
          setMedicoFamiliaNome(doctor?.nome ?? null);
        })
        .catch((err) => setError([err]));
    }
  };

  return (
    <ContainerWithSidebar>
      <GeneralContainer>
        <H1>Marcação de consulta</H1>
        <Spacer bottom={55} />
        <H2>Próximos horários disponíveis</H2>
        <SubTitle>Atendimento {getDescricaoByCodigoAppointmentType(consultType)}</SubTitle>
        <Spacer bottom={16} />
        <SchedulingContainer>
          <SchedulingMedicsContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              <H3 style={{ margin: "0 0 4px 0", color: Colors.SPM600 }}>
                Selecione seu médico de referência:
              </H3>
              <CareLineDropdown
                value={selectedMedicoFamilia ?? null}
                onChange={handleMedicoFamiliaChange}
                items={allMedicosFamilia}
                disabled={false}
                backgroundColor={Colors.SPM50}
                textColor={Colors.SPM600}
                placeholder="Escolha seu médico de referência"
                containerStyle={{ width: "100%" }}
                allValue={undefined}
              />
              <Span style={{ fontSize: 12, color: '#98A2B3', display: 'block' }}>
                Profissional responsável pelo acompanhamento principal do paciente.
              </Span>
            </div>
            <div>
              <LabelInput
                fullWidth
                value={search}
                placeholder="Buscar nome do profissional"
                onChangeText={(event) => {
                  setSearch(event.target.value);
                  if (event.target.value.length === 0 || event.target.value.length > 2) {
                    setSearchValidated(event.target.value || undefined);
                  }
                }}
                leftIcon={
                  <SearchIcon style={{ color: '#98A2B3', marginLeft: 10 }} />
                }
                style={{
                  backgroundColor: '#F2F4F7',
                  borderRadius: '8px',
                  '& .MuiInput-underline:before': { borderBottom: 'none' },
                  '& .MuiInput-underline:after': { borderBottom: 'none' },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                  '& .MuiInputBase-input': {
                    padding: '10px 12px',
                  }
                }}
              />
            </div>
            <Spacer bottom={16} />
            {loading ? (
              <CircularProgress />
            ) : doctorTimeAvailable.length <= 0 ? (
              <Text>Nenhum resultado encontrado</Text>
            ) : null}
            {doctorTimeAvailable.map((item) => (
              <>
                <SchedulingCard data={item} key={item.id} />
              </>
            ))}
          </SchedulingMedicsContainer>
          <SchedulingFilterContainer>
            <H3 style={{ margin: "0 0 4px 0", visibility: "hidden" }}>
              Alinhamento
            </H3>
            <CareLineDropdown
              value={selectedRole}
              onChange={setSelectedRole}
              items={roles}
              disabled={false}
              backgroundColor={Colors.SPM50}
              textColor={Colors.SPM600}
              placeholder="Selecione tipo de profissional"
              containerStyle={{
                width: "100%",
              }}
              allValue={undefined}
            />

            <CareLineDropdown
              value={selectedOccupationalHealth}
              onChange={setSelectedOccupationalHealth}
              items={occupationalHealths}
              disabled={false}
              backgroundColor={Colors.SPM50}
              textColor={Colors.SPM600}
              placeholder="Selecione a unidade"
              containerStyle={{
                width: "100%",
              }}
              allValue={undefined}
            />

            <Calendar
              onChange={setSelectedDay}
              locale={ptBrCustomLocale}
              colorPrimary={Colors.SPM600}
              colorPrimaryLight={Colors.SPM50}
              shouldHighlightWeekends
              minimumDate={day}
              value={selectedDay}
            />
          </SchedulingFilterContainer>
        </SchedulingContainer>
      </GeneralContainer>
      <ErrorArea
        errorList={error}
        onCloseAction={() => {
          setError([]);
        }}
      />
    </ContainerWithSidebar>
  );
}

export default Scheduling;
